import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    collection,
    getDocs,
    getDoc,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";

function OutletMaster() {
    const [userProfile, setUserProfile] = useState(null);
    const [activeTab, setActiveTab] = useState("create"); // create | list | bulk_move | bulk_upload
    const [dsList, setDsList] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // ---------- BULK UPLOAD STATE ----------
    const [bulkPreview, setBulkPreview] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // ---------- BULK REASSIGN STATE ----------
    const [bulkSource, setBulkSource] = useState({ dsId: "", beat: "MON" });
    const [bulkTarget, setBulkTarget] = useState({ dsId: "", beat: "MON" });
    const [reassigning, setReassigning] = useState(false);

    // ---------- CREATE/EDIT OUTLET FORM ----------
    const [newOutlet, setNewOutlet] = useState({
        outletId: "",
        name: "",
        phone: "",
        address: "",
        dsId: "",
        beat: "MON",
        beatName: "",
        lat: "",
        long: "",
    });

    const metrics = React.useMemo(() => {
        const today = new Date().toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
        return {
            total: outlets.length,
            operational: outlets.filter(o => o.status !== "inactive").length,
            activeToday: outlets.filter(o => o.beat === today).length,
            totalDs: dsList.length
        };
    }, [outlets, dsList]);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                setUserProfile(profile);
                if (profile.role === "rsm") {
                    setActiveTab("list");
                }
                loadOutletData(profile);
            }
        });
        return () => unsub();
    }, []);

    const loadOutletData = async (profile) => {
        setLoading(true);
        try {
            // 1. Load DS List
            let dsQ;
            if (profile.role === "distributor") {
                dsQ = query(collection(db, "dsMaster"), where("distributorId", "==", profile.partyId), where("clientId", "==", profile.clientId));
            } else if (profile.role !== "superadmin") {
                dsQ = query(collection(db, "dsMaster"), where("clientId", "==", profile.clientId));
            } else {
                dsQ = query(collection(db, "dsMaster"));
            }
            const dsSnap = await getDocs(dsQ);
            const dsArr = dsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setDsList(dsArr);

            // 2. Load Outlets
            let outletArr = [];
            if (profile.role === "distributor") {
                const idsList = dsArr.map(d => d.dsId);
                if (idsList.length > 0) {
                    const qCount = Math.ceil(idsList.length / 30);
                    for(let i=0; i<qCount; i++) {
                        const batch = idsList.slice(i*30, (i+1)*30);
                        const oQ = query(collection(db, "outletMaster"), where("dsId", "in", batch), where("clientId", "==", profile.clientId));
                        const oSnap = await getDocs(oQ);
                        outletArr = [...outletArr, ...oSnap.docs.map(d => ({ id: d.id, ...d.data() }))];
                    }
                }
            } else if (profile.role !== "superadmin") {
                const oQ = query(collection(db, "outletMaster"), where("clientId", "==", profile.clientId));
                const oSnap = await getDocs(oQ);
                outletArr = oSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            } else {
                const outletSnap = await getDocs(collection(db, "outletMaster"));
                outletArr = outletSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            }
            setOutlets(outletArr);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const onChangeNewOutlet = (field, value) => {
        setNewOutlet((prev) => {
            const updated = { ...prev, [field]: value };

            // Auto-populate beatName if DS or Beat changes
            if (field === "dsId" || field === "beat") {
                const dsId = field === "dsId" ? value : prev.dsId;
                const beat = field === "beat" ? value : prev.beat;

                if (dsId && beat) {
                    const selDs = dsList.find(d => d.dsId === dsId);
                    if (selDs) {
                        const dayKey = beat.toLowerCase() + "Area"; // monArea, tueArea, etc.
                        updated.beatName = selDs[dayKey] || "";
                    }
                }
            }
            return updated;
        });
    };

    const handleSaveOutlet = async (e) => {
        e.preventDefault();
        if (!newOutlet.outletId || !newOutlet.name || !newOutlet.dsId || !newOutlet.beat) {
            alert("Outlet ID, Name, DS, and Beat are required.");
            return;
        }

        const selectedDs = dsList.find((d) => d.dsId === newOutlet.dsId);

        const payload = {
            outletId: newOutlet.outletId.trim(),
            name: newOutlet.name.trim(),
            phone: newOutlet.phone.trim(),
            address: newOutlet.address.trim(),
            dsId: newOutlet.dsId,
            dsName: selectedDs?.name || "",
            beat: newOutlet.beat,
            beatName: newOutlet.beatName.trim(),
            lat: newOutlet.lat.trim(),
            long: newOutlet.long.trim(),
            clientId: userProfile.clientId,
            status: isEditing ? (outlets.find(o => o.id === editingId)?.status || "active") : "active",
        };

        try {
            if (isEditing) {
                await updateDoc(doc(db, "outletMaster", editingId), payload);
                alert("Outlet updated successfully!");
            } else {
                await setDoc(doc(db, "outletMaster", payload.outletId), payload);
                alert("Outlet created successfully!");
            }
            resetForm();
            loadOutletData(userProfile);
            setActiveTab("list");
        } catch (err) {
            console.error(err);
            alert("Error saving outlet");
        }
    };

    const toggleOutletStatus = async (outlet) => {
        const newStatus = outlet.status === "inactive" ? "active" : "inactive";
        try {
            await updateDoc(doc(db, "outletMaster", outlet.id), { status: newStatus });
            loadOutletData(userProfile);
        } catch (err) {
            console.error(err);
            alert("Error updating status");
        }
    };

    const resetForm = () => {
        setNewOutlet({ outletId: "", name: "", phone: "", address: "", dsId: "", beat: "MON", beatName: "", lat: "", long: "" });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditClick = (outlet) => {
        setNewOutlet({
            outletId: outlet.outletId,
            name: outlet.name,
            phone: outlet.phone || "",
            address: outlet.address || "",
            dsId: outlet.dsId,
            beat: outlet.beat || "MON",
            beatName: outlet.beatName || "",
            lat: outlet.lat || "",
            long: outlet.long || "",
        });
        setIsEditing(true);
        setEditingId(outlet.id);
        setActiveTab("create");
    };

    const handleBulkReassign = async () => {
        if (!bulkSource.dsId || !bulkTarget.dsId) {
            alert("Please select both source and target Salesmen.");
            return;
        }
        if (bulkSource.dsId === bulkTarget.dsId && bulkSource.beat === bulkTarget.beat) {
            alert("Source and Target cannot be the same.");
            return;
        }

        const sourceLabel = `${dsList.find(d => d.dsId === bulkSource.dsId)?.name} (${bulkSource.beat})`;
        const targetLabel = `${dsList.find(d => d.dsId === bulkTarget.dsId)?.name} (${bulkTarget.beat})`;

        if (!window.confirm(`Are you sure you want to move ALL outlets from ${sourceLabel} to ${targetLabel}?`)) return;

        setReassigning(true);
        try {
            const targetDs = dsList.find(d => d.dsId === bulkTarget.dsId);
            const targetArea = targetDs[`${bulkTarget.beat.toLowerCase()}Area`] || "";

            const toMove = outlets.filter(o => o.dsId === bulkSource.dsId && o.beat === bulkSource.beat);

            if (toMove.length === 0) {
                alert("No outlets found matching the source criteria.");
                return;
            }

            let count = 0;
            for (const outlet of toMove) {
                await updateDoc(doc(db, "outletMaster", outlet.id), {
                    dsId: bulkTarget.dsId,
                    dsName: targetDs.name,
                    beat: bulkTarget.beat,
                    beatName: targetArea
                });
                count++;
            }

            alert(`Successfully reassigned ${count} outlets!`);
            loadOutletData(userProfile);
            setActiveTab("list");
        } catch (err) {
            console.error(err);
            alert("Error during bulk reassign");
        } finally {
            setReassigning(false);
        }
    };

    const handleBulkFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws);

            const processed = data.map(row => ({
                outletId: String(row.outletId || "").trim(),
                name: String(row.name || "").trim(),
                phone: String(row.phone || "").trim(),
                address: String(row.address || "").trim(),
                dsId: String(row.dsId || "").trim(),
                beat: String(row.beat || "MON").toUpperCase().trim(),
                lat: String(row.lat || "").trim(),
                long: String(row.long || "").trim(),
            })).filter(r => r.outletId && r.name && r.dsId);

            setBulkPreview(processed);
        };
        reader.readAsBinaryString(file);
    };

    const confirmBulkUpload = async () => {
        if (bulkPreview.length === 0) return;
        setIsUploading(true);
        try {
            let count = 0;
            for (const out of bulkPreview) {
                const selDs = dsList.find(d => d.dsId === out.dsId);
                const dayKey = out.beat.toLowerCase() + "Area";

                const payload = {
                    ...out,
                    dsName: selDs?.name || "Unknown DS",
                    beatName: selDs ? (selDs[dayKey] || "") : "",
                    clientId: userProfile.clientId,
                    status: "active",
                };
                await setDoc(doc(db, "outletMaster", out.outletId), payload);
                count++;
            }
            alert(`Successfully uploaded ${count} Outlets!`);
            setBulkPreview([]);
            loadOutletData(userProfile);
            setActiveTab("list");
        } catch (err) {
            console.error(err);
            alert("Error during bulk upload");
        } finally {
            setIsUploading(false);
        }
    };

    const downloadTemplate = () => {
        const headers = ["outletId", "name", "phone", "address", "dsId", "beat", "lat", "long"];
        const data = [
            {
                outletId: "OUTLET001",
                name: "Star Bakery",
                phone: "9123456789",
                address: "123 Main St, Central Market",
                dsId: "DS001",
                beat: "MON",
                lat: "12.9716",
                long: "77.5946"
            }
        ];

        const ws = XLSX.utils.json_to_sheet(data, { header: headers });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Outlets");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, "Outlet_Upload_Template.xlsx");
    };

    const handleDeleteOutlet = async (outlet) => {
        if (!window.confirm(`Delete Outlet "${outlet.name}" ? `)) return;
        try {
            await deleteDoc(doc(db, "outletMaster", outlet.id));
            alert("Deleted.");
            loadOutletData(userProfile);
        } catch (err) {
            console.error(err);
            alert("Error deleting outlet");
        }
    };

    const handleExportData = () => {
        if (outlets.length === 0) {
            alert("No data to export.");
            return;
        }
        
        const exportData = outlets.map(o => ({
            "Outlet ID": o.outletId,
            "Outlet Name": o.name,
            "Phone": o.phone || "",
            "Address": o.address || "",
            "Salesman ID": o.dsId,
            "Salesman Name": o.dsName || "",
            "Day/Beat": o.beat || "",
            "Beat Name": o.beatName || "",
            "Latitude": o.lat || "",
            "Longitude": o.long || "",
            "Status": o.status || "active"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Outlets");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `Outlet_Master_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Hub Registry Matrix</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Manage your distribution points, assign salesmen, and coordinate beats.</p>
                    </div>
                    <div style={{ display: "flex", gap: 30 }}>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--primary)" }}>{metrics.activeToday}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Beats Active Today</div>
                        </div>
                        <div style={{ width: 1, height: 40, background: "var(--border)" }}></div>
                        <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text-main)" }}>{metrics.total}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Hubs</div>
                        </div>
                    </div>
                </div>

                <div style={{ 
                    display: "flex", 
                    gap: 8, 
                    marginBottom: 32, 
                    padding: 6, 
                    background: "var(--surface-hover)", 
                    borderRadius: 14, 
                    width: "fit-content",
                    border: "1px solid var(--border)"
                }}>
                    {[
                        { id: "create", label: isEditing ? "Editing Outlet" : "Manual Entry", hideFor: ["rsm"] },
                        { id: "list", label: "Registry List" },
                        { id: "bulk_move", label: "Network Reassign", hideFor: ["rsm"] },
                        { id: "bulk_upload", label: "Data Synchronize", hideFor: ["rsm"] }
                    ].filter(tab => !tab.hideFor || !tab.hideFor.includes(userProfile?.role)).map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => { resetForm(); setActiveTab(tab.id); }}
                            style={{
                                padding: "10px 20px",
                                borderRadius: 10,
                                border: "none",
                                background: activeTab === tab.id ? "var(--surface)" : "transparent",
                                color: activeTab === tab.id ? "var(--primary)" : "var(--text-muted)",
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: "pointer",
                                transition: "all 0.2s",
                                boxShadow: activeTab === tab.id ? "var(--shadow-sm)" : "none"
                            }}
                        >
                            {tab.id === "create" && (isEditing ? "✏️ " : "➕ ")}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>Initializing network registry...</div>
                ) : activeTab === "create" && userProfile?.role !== "rsm" ? (
                    <div className="premium-card" style={{ maxWidth: 900, padding: 32 }}>
                        <h2 style={{ fontSize: 22, marginBottom: 24 }}>{isEditing ? `System Record: ${editingId}` : "Provision New Outlet"}</h2>
                        <form onSubmit={handleSaveOutlet}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 24, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Registry ID</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="e.g. SHOP-001"
                                        value={newOutlet.outletId}
                                        onChange={(e) => onChangeNewOutlet("outletId", e.target.value)}
                                        required
                                        disabled={isEditing}
                                        style={{ width: "100%", background: isEditing ? "var(--surface-hover)" : "var(--surface)" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Establishment Name</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Enter legal shop name"
                                        value={newOutlet.name}
                                        onChange={(e) => onChangeNewOutlet("name", e.target.value)}
                                        required
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 24 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Primary Salesman</label>
                                    <select
                                        className="form-input"
                                        value={newOutlet.dsId}
                                        onChange={(e) => onChangeNewOutlet("dsId", e.target.value)}
                                        required
                                        style={{ width: "100%" }}
                                    >
                                        <option value="">-- Assign DS --</option>
                                        {dsList.map((ds) => (
                                            <option key={ds.dsId} value={ds.dsId}>
                                                {ds.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Operation Day</label>
                                    <select
                                        className="form-input"
                                        value={newOutlet.beat}
                                        onChange={(e) => onChangeNewOutlet("beat", e.target.value)}
                                        required
                                        style={{ width: "100%" }}
                                    >
                                        {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map(b => (
                                            <option key={b} value={b}>{b}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Specific Beat / Area</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="e.g. Market Square"
                                        value={newOutlet.beatName}
                                        onChange={(e) => onChangeNewOutlet("beatName", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 32 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Latitude Override</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="0.000000"
                                        value={newOutlet.lat}
                                        onChange={(e) => onChangeNewOutlet("lat", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Longitude Override</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="0.000000"
                                        value={newOutlet.long}
                                        onChange={(e) => onChangeNewOutlet("long", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Contact Number</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="+91..."
                                        value={newOutlet.phone}
                                        onChange={(e) => onChangeNewOutlet("phone", e.target.value)}
                                        style={{ width: "100%" }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Full Physical Address</label>
                                <textarea
                                    className="form-input"
                                    value={newOutlet.address}
                                    onChange={(e) => onChangeNewOutlet("address", e.target.value)}
                                    style={{ width: "100%", height: 100, minHeight: 100, resize: "vertical" }}
                                    placeholder="Complete street address for logistics"
                                />
                            </div>

                            <div style={{ display: "flex", gap: 16 }}>
                                <button type="submit" className="btn-primary" style={{ flex: 1, padding: 16, fontSize: 16 }}>
                                    {isEditing ? "Commit Changes" : "Create Record"}
                                </button>
                                {isEditing && (
                                    <button type="button" onClick={resetForm} className="btn-secondary" style={{ padding: "0 32px" }}>
                                        Abort
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                ) : activeTab === "bulk_move" && userProfile?.role !== "rsm" ? (
                    <div className="premium-card" style={{ maxWidth: 800, padding: 32 }}>
                        <h2 style={{ fontSize: 22, marginBottom: 12 }}>Logic Reassignment</h2>
                        <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 32 }}>Migrate entire beat structures between salesmen for network optimization.</p>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginBottom: 40 }}>
                            <div style={{ padding: 24, borderRadius: 16, background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.1)" }}>
                                <h4 style={{ margin: "0 0 16px 0", color: "var(--error)", letterSpacing: "0.05em", fontSize: 12, fontWeight: 800 }}>SOURCE ORIGIN</h4>
                                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                    <select
                                        className="form-input"
                                        value={bulkSource.dsId}
                                        onChange={e => setBulkSource(prev => ({ ...prev, dsId: e.target.value }))}
                                        style={{ width: "100%", background: "white" }}
                                    >
                                        <option value="">-- From Salesman --</option>
                                        {dsList.map(ds => <option key={ds.dsId} value={ds.dsId}>{ds.name}</option>)}
                                    </select>
                                    <select
                                        className="form-input"
                                        value={bulkSource.beat}
                                        onChange={e => setBulkSource(prev => ({ ...prev, beat: e.target.value }))}
                                        style={{ width: "100%", background: "white" }}
                                    >
                                        {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ padding: 24, borderRadius: 16, background: "rgba(34, 197, 94, 0.05)", border: "1px solid rgba(34, 197, 94, 0.1)" }}>
                                <h4 style={{ margin: "0 0 16px 0", color: "var(--success)", letterSpacing: "0.05em", fontSize: 12, fontWeight: 800 }}>TARGET DESTINATION</h4>
                                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                    <select
                                        className="form-input"
                                        value={bulkTarget.dsId}
                                        onChange={e => setBulkTarget(prev => ({ ...prev, dsId: e.target.value }))}
                                        style={{ width: "100%", background: "white" }}
                                    >
                                        <option value="">-- To Salesman --</option>
                                        {dsList.map(ds => <option key={ds.dsId} value={ds.dsId}>{ds.name}</option>)}
                                    </select>
                                    <select
                                        className="form-input"
                                        value={bulkTarget.beat}
                                        onChange={e => setBulkTarget(prev => ({ ...prev, beat: e.target.value }))}
                                        style={{ width: "100%", background: "white" }}
                                    >
                                        {["MON", "TUE", "WED", "THU", "FRI", "SAT"].map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleBulkReassign}
                            className="btn-primary"
                            disabled={reassigning}
                            style={{ width: "100%", padding: 20, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}
                        >
                            {reassigning ? "Executing Matrix Shift..." : "Synchronize Matrix Move"}
                        </button>
                    </div>
                ) : activeTab === "bulk_upload" && userProfile?.role !== "rsm" ? (
                    <div className="premium-card" style={{ maxWidth: 840, padding: 32 }}>
                        <h2 style={{ fontSize: 22, marginBottom: 12 }}>Bulk Ingestion</h2>
                        <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 32 }}>Rapidly provision your entire network using standardized data templates.</p>

                        <div style={{ padding: 24, borderRadius: 16, background: "var(--surface-hover)", border: "2px dashed var(--border)", textAlign: "center", marginBottom: 32 }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
                            <h4 style={{ marginBottom: 8 }}>Drop Network manifest</h4>
                            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 24 }}>System accepts .xlsx files with defined headers</p>
                            
                            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
                                <input 
                                    type="file" 
                                    id="bulk_up" 
                                    style={{ display: "none" }} 
                                    accept=".xlsx, .xls" 
                                    onChange={handleBulkFileUpload} 
                                />
                                <label htmlFor="bulk_up" className="btn-primary" style={{ cursor: "pointer", padding: "12px 24px" }}>
                                    Launch File Explorer
                                </label>
                                <button
                                    onClick={downloadTemplate}
                                    className="btn-secondary"
                                    style={{ padding: "0 24px" }}
                                >
                                    📥 Download Protocol Template
                                </button>
                            </div>
                        </div>

                        {bulkPreview.length > 0 && (
                            <div style={{ animation: "fadeIn 0.3s ease-out" }}>
                                <div style={{ padding: "12px 16px", background: "var(--surface-hover)", borderTopLeftRadius: 12, borderTopRightRadius: 12, border: "1px solid var(--border)", borderBottom: "none", fontSize: 12, fontWeight: 700 }}>
                                    PREVIEW: {bulkPreview.length} RECORDS DETECTED
                                </div>
                                <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "0 0 12px 12px", background: "white" }}>
                                    <table className="enterprise-table">
                                        <thead>
                                            <tr>
                                                <th>Reference ID</th>
                                                <th>Outlet Name</th>
                                                <th>Salesman</th>
                                                <th>Beat Protocol</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkPreview.map((p, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontWeight: 700 }}>{p.outletId}</td>
                                                    <td>{p.name}</td>
                                                    <td>{p.dsId}</td>
                                                    <td><span className="tb-status-pill" style={{ background: "var(--surface-hover)", color: "var(--text-main)" }}>{p.beat}</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    onClick={confirmBulkUpload}
                                    disabled={isUploading}
                                    className="btn-primary"
                                    style={{ width: "100%", marginTop: 24, padding: 16 }}
                                >
                                    {isUploading ? "Committing Hub Registry..." : `Finalize ${bulkPreview.length} Records`}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                        <div style={{ padding: "24px 32px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--surface-hover)" }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>Fleet Registry</h3>
                                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{outlets.length} Authorized Distribution Points</div>
                            </div>
                            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                                <button 
                                    onClick={handleExportData}
                                    className="btn-secondary"
                                    style={{ padding: "10px 20px", fontSize: 13, borderRadius: 10, display: "flex", alignItems: "center", gap: 8 }}
                                >
                                    📥 Export Registry
                                </button>
                                <div style={{ position: "relative" }}>
                                    <input 
                                        type="text" 
                                        className="form-input" 
                                        placeholder="Find hub by name, ID or phone..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ width: 300, paddingLeft: 40, fontSize: 13 }} 
                                    />
                                    <span style={{ position: "absolute", left: 14, top: 10, opacity: 0.5 }}>🔍</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ maxHeight: 750, overflowY: "auto" }}>
                            <table className="enterprise-table">
                                <thead>
                                    <tr>
                                        <th style={{ paddingLeft: 32 }}>HUB SPECIFICATION</th>
                                        <th>LOGISTICS PROTOCOL</th>
                                        <th>ASSIGNED EXECUTIVE</th>
                                        <th>OPERATIONAL STATUS</th>
                                        {userProfile?.role !== "rsm" && <th style={{ textAlign: "right", paddingRight: 32 }}>HUB MANAGEMENT</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {outlets.filter(o => 
                                        o.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                        o.outletId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        (o.phone || "").includes(searchTerm)
                                    ).length === 0 ? (
                                        <tr><td colSpan="5" style={{ textAlign: "center", padding: 100, color: "var(--text-muted)" }}>
                                            <div style={{ fontSize: 48, marginBottom: 16 }}>🛰️</div>
                                            <div style={{ fontWeight: 800 }}>No Hubs Detected</div>
                                            <p style={{ fontSize: 13 }}>Adjust search parameters or provision a new outlet.</p>
                                        </td></tr>
                                    ) : (
                                        outlets.filter(o => 
                                            o.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                            o.outletId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (o.phone || "").includes(searchTerm)
                                        ).map((o) => (
                                            <tr key={o.id}>
                                                <td style={{ paddingLeft: 32 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                                        {o.photoUrl ? (
                                                            <img src={o.photoUrl} alt="hub" style={{ width: 44, height: 44, borderRadius: 12, objectFit: "cover", border: "1px solid var(--border)" }} />
                                                        ) : (
                                                            <div style={{ width: 44, height: 44, background: "var(--primary)10", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "var(--primary)", fontWeight: 900 }}>HUB</div>
                                                        )}
                                                        <div>
                                                            <div style={{ fontWeight: 800, color: "var(--text-main)", fontSize: 15 }}>{o.name}</div>
                                                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>REF: {o.outletId} • TEL: {o.phone || "UNVERIFIED"}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                        <span style={{ padding: "4px 10px", borderRadius: 8, background: "var(--sidebar-bg)", color: "white", fontSize: 10, fontWeight: 900 }}>{o.beat}</span>
                                                        <div style={{ fontSize: 13, fontWeight: 700 }}>{o.beatName || "UNGATED AREA"}</div>
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.address || (o.lat ? `${o.lat}, ${o.long}` : "No geo-marker")}</div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-main)" }}>{o.dsName}</div>
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>NETWORK ID: {o.dsId}</div>
                                                </td>
                                                <td>
                                                    <div
                                                        onClick={() => userProfile?.role !== "rsm" && toggleOutletStatus(o)}
                                                        className={`tb-status-pill ${o.status === "inactive" ? "status-offline" : "status-online"}`}
                                                        style={{ cursor: userProfile?.role !== "rsm" ? "pointer" : "default", display: "inline-flex" }}
                                                    >
                                                        <div className="status-dot"></div>
                                                        {o.status === "inactive" ? "DISCONNECTED" : "OPERATIONAL"}
                                                    </div>
                                                </td>
                                                {userProfile?.role !== "rsm" && (
                                                    <td style={{ textAlign: "right", paddingRight: 32 }}>
                                                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                                            <button onClick={() => handleEditClick(o)} className="btn-secondary" style={{ padding: "8px 16px", fontSize: 11, borderRadius: 8 }}>RE-CALIBRATE</button>
                                                            <button onClick={() => handleDeleteOutlet(o)} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "rgba(239, 68, 68, 0.08)", color: "var(--error)", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>RETIRE</button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default OutletMaster;
